"use client";

import { PropsWithChildren } from "react";
import { usePathname } from "next/navigation";
import { useSession, signIn } from "next-auth/react";
import { UniversalLayout } from "@zapier/universal-layout";
import {
  Alert,
  AlertDescription,
  Button,
  ContentSection,
  Spinner,
  Surface,
  Text,
} from "@zapier/design-system";

import { getUrls } from "@utils/urls";

export default function AuthedPage({ children }: PropsWithChildren) {
  const pathname = usePathname();
  const { data: session, status } = useSession();
  const isLoading = status === "loading";
  const isAuthenticated =
    status === "authenticated" && session?.user?.is_logged_in;
  const isStaff = session?.user?.is_staff ?? false;
  const { apiBase } = getUrls();

  const trackEvent = () => {
    // TODO: Implement event tracking
  };

  const layoutProps = {
    currentUrl: pathname || "",
    mainContentId: "ticketcopilot-main",
    mode: "in-app" as const,
    trackEvent,
    apiHostname: apiBase,
    urlPathPrefix: apiBase,
  };

  if (isLoading) {
    return (
      <UniversalLayout {...layoutProps}>
        <ContentSection size="small">
          <div style={{ margin: "3rem auto" }}>
            <Surface
              backgroundColor="BackgroundWeaker"
              borderColor="GrayWarm3"
              shadow="Shadow1"
            >
              <div style={{ padding: "3rem", textAlign: "center" }}>
                <Spinner
                  size="large"
                  aria-label="Loading authentication status"
                />
                <Text type="Body2" color="GrayWarm6" margin="medium 0 0">
                  Checking your authentication...
                </Text>
              </div>
            </Surface>
          </div>
        </ContentSection>
      </UniversalLayout>
    );
  }

  // If not authenticated, show sign in
  if (!isAuthenticated) {
    return (
      <UniversalLayout {...layoutProps}>
        <ContentSection size="small">
          <div style={{ margin: "3rem auto" }}>
            <Surface
              backgroundColor="PrimeWhite"
              borderColor="GrayWarm3"
              shadow="Shadow2"
            >
              <div style={{ padding: "3rem" }}>
                <Alert status="info" variant="default" title="Sign in required">
                  <AlertDescription>
                    You need to be signed in to access this page. Please log in
                    to continue.
                  </AlertDescription>
                </Alert>
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    justifyContent: "center",
                    marginTop: "1.5rem",
                  }}
                >
                  <Button onClick={() => signIn("zapier")} variant="primary">
                    Sign in with Zapier
                  </Button>
                </div>
              </div>
            </Surface>
          </div>
        </ContentSection>
      </UniversalLayout>
    );
  }

  if (!isStaff) {
    return (
      <UniversalLayout {...layoutProps}>
        <ContentSection size="small">
          <div style={{ margin: "3rem auto" }}>
            <Surface
              backgroundColor="PrimeWhite"
              borderColor="GrayWarm3"
              shadow="Shadow2"
            >
              <div style={{ padding: "3rem" }}>
                <Alert
                  status="error"
                  variant="default"
                  title="Oops! You're not authorized to access this page."
                >
                  <AlertDescription>
                    This page is restricted to Zapier staff members only. If you
                    believe you should have access, please contact your
                    administrator.
                  </AlertDescription>
                </Alert>
                <Text
                  type="Body2"
                  color="GrayWarm6"
                  margin="large 0 0"
                  style={{ textAlign: "center" }}
                >
                  Need help? Contact your workspace admin for assistance.
                </Text>
              </div>
            </Surface>
          </div>
        </ContentSection>
      </UniversalLayout>
    );
  }

  return (
    <UniversalLayout
      {...layoutProps}
      mode="navigation"
      renderAlertBanner={() => null}
      renderFooter={() => null}
      renderHeader={() => null}
    >
      {children}
    </UniversalLayout>
  );
}
